// Workflow control actions hook as specified in appmark.txt
import { useState, useCallback } from "react";
import { apiService } from "../services/api";

interface WorkflowActionsState {
  loading: Record<string, boolean>;
  error: string | null;
}

export const useWorkflowActions = (onSuccess?: () => void, onOptimisticUpdate?: (workflowId: string, status: string) => void) => {
  const [state, setState] = useState<WorkflowActionsState>({
    loading: {},
    error: null,
  });

  const setLoading = useCallback((workflowId: string, loading: boolean) => {
    setState((prev) => ({
      ...prev,
      loading: { ...prev.loading, [workflowId]: loading },
      error: null,
    }));
  }, []);

  const setError = useCallback((error: string) => {
    setState((prev) => ({ ...prev, error }));
  }, []);

  // Pause workflow
  const pauseWorkflow = useCallback(
    async (workflowId: string) => {
      try {
        setLoading(workflowId, true);
        // Optimistically update UI to "paused" immediately
        onOptimisticUpdate?.(workflowId, "paused");
        await apiService.pauseWorkflow(workflowId);
        console.log(`Workflow ${workflowId} paused successfully`);
        onSuccess?.(); // Refresh data after successful pause
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to pause workflow";
        setError(message);
        console.error("Failed to pause workflow:", error);
        // Revert optimistic update on error
        onSuccess?.();
        throw error;
      } finally {
        setLoading(workflowId, false);
      }
    },
    [setLoading, setError, onSuccess, onOptimisticUpdate]
  );

  // Resume workflow
  const resumeWorkflow = useCallback(
    async (workflowId: string) => {
      try {
        setLoading(workflowId, true);
        // Optimistically update UI to "running" immediately
        onOptimisticUpdate?.(workflowId, "running");
        await apiService.resumeWorkflow(workflowId);
        console.log(`Workflow ${workflowId} resumed successfully`);
        onSuccess?.(); // Refresh data after successful resume
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to resume workflow";
        setError(message);
        console.error("Failed to resume workflow:", error);
        // Revert optimistic update on error
        onSuccess?.();
        throw error;
      } finally {
        setLoading(workflowId, false);
      }
    },
    [setLoading, setError, onSuccess, onOptimisticUpdate]
  );

  // Change workflow priority
  const changeWorkflowPriority = useCallback(
    async (workflowId: string, priority: "low" | "medium" | "high") => {
      try {
        setLoading(workflowId, true);
        await apiService.changeWorkflowPriority(workflowId, priority);
        console.log(`Workflow ${workflowId} priority changed to ${priority}`);
        onSuccess?.(); // Refresh data after successful priority change
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Failed to change workflow priority";
        setError(message);
        console.error("Failed to change workflow priority:", error);
        throw error;
      } finally {
        setLoading(workflowId, false);
      }
    },
    [setLoading, setError, onSuccess]
  );

  // Approve request
  const approveRequest = useCallback(
    async (approvalId: string) => {
      try {
        setLoading(approvalId, true);
        await apiService.approveRequest(approvalId);
        console.log(`Request ${approvalId} approved successfully`);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to approve request";
        setError(message);
        console.error("Failed to approve request:", error);
        throw error;
      } finally {
        setLoading(approvalId, false);
      }
    },
    [setLoading, setError]
  );

  // Reject request
  const rejectRequest = useCallback(
    async (approvalId: string, comment: string = "Rejected via workflow") => {
      try {
        setLoading(approvalId, true);
        await apiService.rejectRequest(approvalId, comment);
        console.log(`Request ${approvalId} rejected successfully`);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to reject request";
        setError(message);
        console.error("Failed to reject request:", error);
        throw error;
      } finally {
        setLoading(approvalId, false);
      }
    },
    [setLoading, setError]
  );

  // Archive workflow
  const archiveWorkflow = useCallback(
    async (workflowId: string) => {
      try {
        setLoading(workflowId, true);
        await apiService.archiveWorkflow(workflowId);
        console.log(`Workflow ${workflowId} archived successfully`);

        // Delay to ensure database transaction is committed before refreshing
        // Increased to 500ms to ensure DB transaction completes
        await new Promise((resolve) => setTimeout(resolve, 500));

        onSuccess?.(); // Refresh data after successful archive
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to archive workflow";
        setError(message);
        console.error("Failed to archive workflow:", error);
        throw error;
      } finally {
        setLoading(workflowId, false);
      }
    },
    [setLoading, setError, onSuccess]
  );

  // Toggle workflow (pause/resume based on current status)
  const toggleWorkflow = useCallback(
    async (workflowId: string, currentStatus: string) => {
      if (currentStatus === "running") {
        await pauseWorkflow(workflowId);
      } else if (currentStatus === "paused" || currentStatus === "waiting_approval") {
        await resumeWorkflow(workflowId);
      }
    },
    [pauseWorkflow, resumeWorkflow]
  );

  // Clear error
  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  return {
    loading: state.loading,
    error: state.error,
    pauseWorkflow,
    resumeWorkflow,
    changeWorkflowPriority,
    archiveWorkflow,
    approveRequest,
    rejectRequest,
    toggleWorkflow,
    clearError,
  };
};
